import React, { useState, useEffect, useMemo } from "react";
import {
  ChevronDown,
  User,
  Eye,
  EyeOff,
  LogIn,
  Clock,
  Shield,
  Coffee,
  Users,
  Package,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { getDeviceName, getOrCreateDeviceId } from "../utils/device";
import { withCacheBuster } from "../utils/cacheBuster";
import {
  getLockedSessions,
  migrateLegacyLockedSession,
} from "../utils/lockedSessions";
import { extractBusinessType, getStoredBusinessType } from "../utils/businessType";

const API_BASE = "/api";
const SLIDE_DURATION_MS = 4000;
const PROGRESS_TICK_MS = 40;

// Avatar fallback identique à l'admin (avatar.vercel.sh)
const getAvatarUrl = (user) => {
  const seed =
    user?.username ||
    `${user?.first_name || ""}-${user?.last_name || ""}` ||
    "user";
  const initials = `${(user?.first_name || "M")[0] || ""}${(user?.last_name || "S")[0] || ""}`;
  return (
    user?.avatar_url ||
    `https://avatar.vercel.sh/${encodeURIComponent(seed)}.svg?text=${encodeURIComponent(
      initials,
    )}`
  );
};

const getUserDisplayName = (user) => {
  const firstName = String(user?.first_name || "").trim();
  const lastName = String(user?.last_name || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;
  const username = String(user?.username || "").trim();
  if (username) return username;
  return "Utilisateur";
};

// Images de carrousel inspirantes (déplacé hors du composant pour éviter les dépendances inutiles)
const restaurantCarouselImages = [
  {
    url: withCacheBuster("/images/login-slide-restaurant-1.jpg"),
    title: "Plus qu'un logiciel",
    subtitle: "Moo Sync est le compagnon idéal pour les restaurants en Afrique",
  },
  {
    url: withCacheBuster("/images/login-slide-gastro-4.jpg"),
    title: "Expérience gastronomique",
    subtitle: "Offrez à vos clients une expérience culinaire raffinée, du service au paiement",
  },
  {
    url: withCacheBuster("/images/PHOTO-2025-06-13-19-19-04.jpg"),
    title: "En cuisine ou bar",
    subtitle: "Recevez les commandes sur un écran assisté vocalement",
  },
];

const boutiqueCarouselImages = [
  {
    url: withCacheBuster("/images/uchico.jpg"),
    title: "Commerce fluide",
    subtitle: "Moo Sync Boutique simplifie chaque passage en caisse",
  },
  {
    url: withCacheBuster("/images/PHOTO-2025-06-13-19-19-04.jpg"),
    title: "Stocks & ventes",
    subtitle: "Suivez vos ventes et votre catalogue en temps réel",
  },
  {
    url: withCacheBuster("/images/PHOTO-2025-06-13-19-49-02.jpg"),
    title: "Recherche instantanée",
    subtitle: "Nom ou SKU, trouvez le produit en un geste",
  },
  {
    url: withCacheBuster("/images/FB5996AD-BB84-417D-B96E-DA4E2A4ECB5B.jpg"),
    title: "Expérience client",
    subtitle: "Un encaissement moderne, simple et rapide",
  },
];

const normalizeLoginRole = (value) => {
  const role = String(value || "").trim().toLowerCase();
  const aliases = {
    cuisine: "kitchen",
    kitchen: "kitchen",
    bar: "bar",
    caissier: "cashier",
    caisse: "cashier",
    cashier: "cashier",
  };
  return aliases[role] || role;
};

const Login = ({ onLogin, forceSuperadmin = false, requiredRole = null }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sessionConflict, setSessionConflict] = useState(null);
  const [activeSessionsByUserId, setActiveSessionsByUserId] = useState({});
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [slideProgress, setSlideProgress] = useState(0);
  const [lockedSessions, setLockedSessions] = useState({});
  const [licenseState, setLicenseState] = useState({
    blocked: false,
    message: "",
    reason: "ok",
  });
  const [businessType, setBusinessType] = useState(getStoredBusinessType);
  const isBoutique = businessType === "boutique";
  const carouselImages = useMemo(
    () => (isBoutique ? boutiqueCarouselImages : restaurantCarouselImages),
    [isBoutique],
  );
  const currentDeviceId = useMemo(() => getOrCreateDeviceId(), []);
  const isLicenseBlockedForScreen = Boolean(licenseState?.blocked) && !forceSuperadmin;
  const canUsePinInput = Boolean(selectedUser) && !loading && !isLicenseBlockedForScreen;
  const getDigitFromEvent = (event) => {
    if (event.key >= "0" && event.key <= "9") {
      return event.key;
    }
    const code = event.code || "";
    if (code.startsWith("Digit")) {
      return code.slice(5);
    }
    if (code.startsWith("Numpad")) {
      return code.slice(6);
    }
    return null;
  };

  useEffect(() => {
    migrateLegacyLockedSession();
    setLockedSessions(getLockedSessions());
    fetchLicenseStatus();
    fetchUsers();
    refreshActiveSessions();

    return undefined;
  }, [carouselImages.length]);

  useEffect(() => {
    if (!carouselImages.length) return undefined;

    const step = (100 * PROGRESS_TICK_MS) / SLIDE_DURATION_MS;
    const timer = setInterval(() => {
      setSlideProgress((prev) => {
        const next = prev + step;
        if (next >= 100) {
          setCurrentImageIndex((idx) => (idx + 1) % carouselImages.length);
          return 0;
        }
        return next;
      });
    }, PROGRESS_TICK_MS);

    return () => clearInterval(timer);
  }, [carouselImages.length]);

  useEffect(() => {
    if (typeof window === "undefined" || !Array.isArray(carouselImages)) return undefined;
    const preloadTargets = carouselImages.slice(0, 3);
    const preloadImages = [];
    preloadTargets.forEach((item, idx) => {
      const src = String(item?.url || "").trim();
      if (!src) return;
      const img = new window.Image();
      img.decoding = "async";
      img.loading = "eager";
      if (idx === 0) {
        img.fetchPriority = "high";
      }
      img.src = src;
      if (typeof img.decode === "function") {
        img.decode().catch(() => {});
      }
      preloadImages.push(img);
    });
    return () => {
      preloadImages.length = 0;
    };
  }, [carouselImages]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchLicenseStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshActiveSessions();
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setCurrentImageIndex(0);
    setSlideProgress(0);
  }, [isBoutique]);

  useEffect(() => {
    const loadBusinessType = async () => {
      try {
        const res = await fetch("/api/restaurant-info");
        if (!res.ok) return;
        const data = await res.json();
        const nextType = extractBusinessType(data);
        setBusinessType(nextType);
        if (typeof localStorage !== "undefined") {
          try {
            localStorage.setItem("restaurant-info", JSON.stringify(data));
          } catch (_e) {}
        }
      } catch (_e) {
        // ignore fetch errors
      }
    };
    loadBusinessType();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!canUsePinInput) return;
      const target = event.target;
      const tag = target?.tagName?.toLowerCase?.() || "";
      if (target?.isContentEditable || ["input", "textarea", "select"].includes(tag)) {
        return;
      }

      const digit = getDigitFromEvent(event);
      if (digit !== null) {
        event.preventDefault();
        handlePinInput(digit);
        return;
      }

      if (event.key === "Backspace") {
        event.preventDefault();
        handlePinDelete();
        return;
      }

      if (event.key === "Enter") {
        if (pin.length === 6) {
          event.preventDefault();
          handleSubmit({ preventDefault: () => {} });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [canUsePinInput, pin]);

  const fetchUsers = async () => {
    try {
      const query = forceSuperadmin ? "?superadmin=1" : "";
      const response = await fetch(`${API_BASE}/auth/users${query}`);
      if (response.ok) {
        const data = await response.json();
        if (forceSuperadmin) {
          const superadmins = data.filter((u) => u.role === "superadmin");
          setUsers(superadmins);
          setSelectedUser(superadmins[0] || null);
          setShowUserDropdown(false);
        } else {
          const normalizedRequiredRole = normalizeLoginRole(requiredRole);
          const visibleUsers = ["kitchen", "bar"].includes(normalizedRequiredRole)
            ? data.filter(
                (user) =>
                  normalizeLoginRole(user?.role) === normalizedRequiredRole ||
                  ["manager", "admin", "superadmin"].includes(
                    normalizeLoginRole(user?.role),
                  ),
              )
            : data;
          setUsers(visibleUsers);
          if (visibleUsers.length === 1) {
            setSelectedUser(visibleUsers[0]);
          }
        }
      }
    } catch (error) {
      console.error("Erreur chargement utilisateurs:", error);
    }
  };

  const fetchLicenseStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/license/status`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-store",
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      setLicenseState({
        blocked: Boolean(data?.blocked),
        message: data?.message || "",
        reason: data?.reason || "ok",
      });
    } catch (_error) {
      // non bloquant pour la connexion locale
    }
  };

  const refreshActiveSessions = async () => {
    try {
      const response = await fetch(`${API_BASE}/auth/active-sessions`, {
        headers: {
          "X-Moo-Device-Id": currentDeviceId,
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      const sessions = Array.isArray(data?.sessions) ? data.sessions : [];
      const map = {};
      for (const session of sessions) {
        if (!session?.userId) continue;
        map[session.userId] = session;
      }
      setActiveSessionsByUserId(map);
      migrateLegacyLockedSession();
      setLockedSessions(getLockedSessions());
    } catch (_e) {
      // non bloquant
    }
  };

  // Fonction pour vérifier si un utilisateur a une session active
  const isUserActive = (user) => {
    const userId = user?.id;
    if (!userId) return false;
    // Vérifier s'il y a une session verrouillée pour cet utilisateur
    if (lockedSessions[String(userId)]) {
      return true;
    }

    const session = activeSessionsByUserId[userId] || null;
    if (!session) return false;
    if (!currentDeviceId || !session.deviceId) return true;
    return session.deviceId !== currentDeviceId;
  };

  const isUserNetworkConnected = (user) => {
    const userId = user?.id;
    const session = userId ? activeSessionsByUserId[userId] : null;
    if (!session) return false;
    if (!currentDeviceId || !session.deviceId) return true;
    return session.deviceId === currentDeviceId;
  };

  const handlePinInput = (digit) => {
    if (!canUsePinInput) return;
    if (pin.length < 6) {
      setPin((prev) => prev + digit);
      setError("");
    }
  };

  const handlePinDelete = () => {
    if (!canUsePinInput) return;
    setPin((prev) => prev.slice(0, -1));
    setError("");
  };

  const submitPinLogin = async ({ takeover = false } = {}) => {
    const deviceId = getOrCreateDeviceId();
    const deviceName = getDeviceName();

    const response = await fetch(`${API_BASE}/auth/pin`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Moo-Device-Id": deviceId,
        "X-Moo-Device-Name": deviceName,
      },
      credentials: "include",
      body: JSON.stringify({
        userId: selectedUser.id,
        pin,
        takeover,
        superadminLogin: forceSuperadmin,
        deviceId,
        deviceName,
      }),
    });

    return response;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLicenseBlockedForScreen) {
      setError(
        licenseState?.message ||
          "Abonnement expiré. Contactez support@djizef.com",
      );
      return;
    }

    if (!selectedUser) {
      setError("Veuillez sélectionner un utilisateur");
      return;
    }

    if (pin.length !== 6) {
      setError("Le PIN doit contenir 6 chiffres");
      return;
    }

    setLoading(true);
    setError("");
    setSessionConflict(null);

    try {
      const response = await submitPinLogin();

      if (response.ok) {
        const data = await response.json();
        const loggedUser = forceSuperadmin
          ? { ...data.user, role: "superadmin" }
          : data.user;
        const userKey = forceSuperadmin ? "moo_user_superadmin" : "moo_user";
        const tokenKey = forceSuperadmin ? "moo_token_superadmin" : "moo_token";
        localStorage.setItem(userKey, JSON.stringify(loggedUser));
        localStorage.setItem(tokenKey, data.token);

        onLogin(loggedUser, data.token);
      } else if (response.status === 409) {
        const conflictData = await response.json().catch(() => null);
        setSessionConflict(conflictData || { error: "SESSION_ACTIVE" });
      } else {
        const errorData = await response.json();
        const msg = errorData.error || "PIN incorrect";
        setError(msg);
        toast.error("Mot de passe incorrect");
        setPin("");
      }
    } catch (error) {
      setError("Erreur de connexion au serveur");
      toast.error("Mot de passe incorrect");
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    const icons = {
      admin: Shield,
      manager: Users,
      cashier: LogIn,
      server: Coffee,
      stockeur: Package,
      superadmin: Shield,
    };
    return icons[role] || User;
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: "from-red-600 to-red-400",
      manager: "from-blue-600 to-blue-400",
      cashier: "from-green-600 to-green-400",
      server: "from-yellow-600 to-yellow-400",
      stockeur: "from-teal-600 to-emerald-400",
      superadmin: "from-purple-600 to-purple-400",
    };
    return colors[role] || "from-gray-600 to-gray-400";
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: "Administrateur",
      manager: "Manager",
      cashier: "Caissier",
      server: "Serveur",
      stockeur: "Stockeur",
      superadmin: "Superadmin",
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex relative">
      {isLicenseBlockedForScreen && (
        <div className="fixed top-4 right-4 z-[120] max-w-md rounded-2xl border border-red-400/50 bg-red-500/15 px-4 py-3 text-sm text-red-100 shadow-2xl backdrop-blur">
          {licenseState?.message ||
            "Votre abonnement MOO Sync a expiré. Veuillez vous rapprocher de votre prestataire ou nous écrire à support@djizef.com"}
        </div>
      )}
      {/* Section gauche - Carrousel d'images */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        {carouselImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === currentImageIndex ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent z-10" />
            <img
              src={image.url}
              alt={image.title}
              className="w-full h-full object-cover object-center transition-transform duration-1000 hover:scale-105"
              loading="eager"
              decoding="async"
              fetchPriority={index === 0 ? "high" : "auto"}
              onError={(e) => {
                console.error("Erreur chargement image:", image.url);
                e.target.style.display = "none";
              }}
            />
            <div className="absolute bottom-0 left-0 right-0 p-8 z-20">
              <h2 className="text-4xl font-bold text-white mb-2">
                {image.title}
              </h2>
              <p className="text-xl text-gray-200">{image.subtitle}</p>
              <div className="mt-4 flex items-center gap-2">
                {carouselImages.map((_, barIndex) => (
                  <div
                    key={`story-bar-${barIndex}`}
                    className="h-1 flex-1 rounded-full bg-white/30 overflow-hidden"
                  >
                    <div
                      className="h-full rounded-full bg-yellow-400"
                      style={{
                        width:
                          barIndex < currentImageIndex
                            ? "100%"
                            : barIndex === currentImageIndex
                              ? `${slideProgress}%`
                              : "0%",
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Logo MOO en overlay */}
        <div className="absolute top-8 left-8 z-30">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center">
              <img
                src={withCacheBuster("/moo-sync-insigne-logo.png")}
                alt="MOO Sync Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h1 className="text-2xl font-black text-white">MOO Sync</h1>
              <p className="text-sm text-gray-300">Système de caisse</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section droite - Connexion PIN */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Header mobile (visible seulement sur mobile) */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg">
                <img
                  src={withCacheBuster("/moo-sync-insigne-logo.png")}
                  alt="MOO Sync Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-3xl font-black text-white">MOO Sync</h1>
                <p className="text-gray-400">Système de caisse</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-gray-700/50 shadow-2xl">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">
                {forceSuperadmin ? "Connexion Superadmin" : "Se connecter"}
              </h2>
                <p className="text-gray-400">
                  {forceSuperadmin
                  ? "Saisissez le PIN Superadmin pour accéder à l’administration"
                  : "Choisissez votre profil et saisissez votre PIN"}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Sélection utilisateur */}
              {!forceSuperadmin ? (
                <div className="relative">
                  <label className="block text-sm font-semibold text-gray-300 mb-3">
                    Sélectionner un utilisateur
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowUserDropdown(!showUserDropdown)}
                      disabled={isLicenseBlockedForScreen}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-4 text-left text-white flex items-center justify-between hover:bg-gray-700/70 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {selectedUser ? (
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-600 border border-gray-500">
                            <img
                              src={getAvatarUrl(selectedUser)}
                              alt={getUserDisplayName(selectedUser)}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                if (e.target.dataset.fallbackApplied) return;
                                e.target.dataset.fallbackApplied = "true";
                                e.target.src = getAvatarUrl({});
                              }}
                            />
                          </div>
                          <div>
                            <p className="font-semibold">
                              {getUserDisplayName(selectedUser)}
                            </p>
                            <p className="text-sm text-gray-400">
                              {getRoleLabel(selectedUser.role)}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">
                          Choisir un utilisateur...
                        </span>
                      )}
                      <ChevronDown
                        className={`w-5 h-5 transition-transform ${showUserDropdown ? "rotate-180" : ""}`}
                      />
                    </button>

                    {showUserDropdown && (
                      <div className="absolute top-full left-0 right-0 mt-2 bg-gray-700 border border-gray-600 rounded-xl shadow-2xl z-10 max-h-60 overflow-y-auto">
                        {users.map((user) => {
                          const avatar = getAvatarUrl(user);
                          return (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                setSelectedUser(user);
                                setShowUserDropdown(false);
                                setPin("");
                                setError("");
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-gray-600 transition-colors border-b border-gray-600 last:border-b-0 flex items-center space-x-3"
                            >
                              <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-600 border border-gray-500">
                                <img
                                  src={avatar}
                                  alt={getUserDisplayName(user)}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    if (e.target.dataset.fallbackApplied) return;
                                    e.target.dataset.fallbackApplied = "true";
                                    e.target.src = getAvatarUrl({});
                                  }}
                                />
                              </div>
                              <div>
                                <p className="font-semibold text-white">
                                  {getUserDisplayName(user)}
                                </p>
                                <div className="flex items-center space-x-3">
                                  <span className="text-sm text-gray-400">
                                    {getRoleLabel(user.role)}
                                  </span>
                                  {isUserNetworkConnected(user) ? (
                                    <div className="flex items-center space-x-1 text-xs text-emerald-400">
                                      <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                                      <span>Déjà connecté</span>
                                    </div>
                                  ) : isUserActive(user) ? (
                                    <div className="flex items-center space-x-1 text-xs text-amber-300">
                                      <div className="w-2 h-2 bg-amber-300 rounded-full animate-pulse"></div>
                                      <span>Session verrouillée</span>
                                    </div>
                                  ) : user.last_login ? (
                                    <div className="flex items-center space-x-1 text-xs text-gray-500">
                                      <Clock size={12} />
                                      <span>Dernière connexion</span>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ) : selectedUser ? (
                <div className="rounded-2xl border border-purple-500/40 bg-purple-900/10 px-4 py-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-400 rounded-lg flex items-center justify-center">
                    <Shield size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-300">Compte sélectionné</p>
                    <p className="text-lg font-semibold text-white">
                      {getUserDisplayName(selectedUser) || "Superadmin"}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-red-400 bg-red-600/10 border border-red-600/40 rounded-xl p-3">
                  Aucun compte superadmin trouvé. Veuillez vérifier le seed.
                </div>
              )}

              {/* Affichage PIN */}
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-3">
                  Code PIN (6 chiffres)
                </label>
                <div className="flex items-center justify-center space-x-3 mb-6">
                  {[...Array(6)].map((_, index) => (
                    <div
                      key={index}
                      className={`w-12 h-12 rounded-xl border-2 flex items-center justify-center text-xl font-bold transition-colors ${
                        pin[index]
                          ? "border-yellow-500 bg-yellow-500/20 text-yellow-400"
                          : "border-gray-600 bg-gray-700/50 text-gray-500"
                      }`}
                    >
                      {pin[index] ? (showPin ? pin[index] : "•") : ""}
                    </div>
                  ))}
                </div>

                {/* Toggle affichage PIN */}
                <div className="flex justify-center mb-6">
                  <button
                    type="button"
                    onClick={() => setShowPin(!showPin)}
                    className="flex items-center space-x-2 text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                    <span className="text-sm">
                      {showPin ? "Masquer" : "Afficher"} le PIN
                    </span>
                  </button>
                </div>

                {/* Pavé numérique */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, "", 0, "⌫"].map((key, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        if (key === "⌫") {
                          handlePinDelete();
                        } else if (key !== "") {
                          handlePinInput(key.toString());
                        }
                      }}
                      disabled={!canUsePinInput || key === ""}
                      className={`h-14 rounded-xl font-bold text-lg transition-all ${
                        key === ""
                          ? "invisible"
                          : !canUsePinInput
                            ? "bg-gray-800/40 border border-gray-700 text-gray-500 cursor-not-allowed"
                          : key === "⌫"
                            ? "bg-red-600/20 border border-red-600/50 text-red-400 hover:bg-red-600/30"
                            : "bg-gray-700/50 border border-gray-600 text-white hover:bg-gray-600/70 hover:border-yellow-500/50 active:scale-95"
                      }`}
                    >
                      {key}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message d'erreur */}
              {error && null}

              {/* Bouton de connexion */}
              <button
                type="submit"
                disabled={
                  loading ||
                  !selectedUser ||
                  pin.length !== 6 ||
                  isLicenseBlockedForScreen
                }
                className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-4 rounded-xl hover:from-yellow-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <LogIn size={20} />
                    <span>Se connecter</span>
                  </>
                )}
              </button>
            </form>

          </div>
        </div>
      </div>

      {/* Conflit de session (compte déjà connecté sur un autre appareil) */}
      {sessionConflict?.error === "SESSION_ACTIVE" && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4">
          <div className="w-full max-w-lg rounded-3xl border border-white/10 bg-gray-900/90 shadow-2xl p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-amber-300">
                  Sécurité
                </p>
                <h3 className="text-xl font-bold text-white mt-1">
                  Compte déjà connecté
                </h3>
                <p className="text-sm text-white/70 mt-1">
                  Ce compte est déjà utilisé sur un autre appareil. Pour continuer
                  ici, vous pouvez prendre la main.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSessionConflict(null)}
                className="shrink-0 p-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition"
                aria-label="Fermer"
              >
                <X className="w-5 h-5 text-white/80" />
              </button>
            </div>

            {sessionConflict?.activeSession && (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-white/60">Appareil :</span>
                  <span className="font-semibold text-white">
                    {sessionConflict.activeSession.deviceName || "Inconnu"}
                  </span>
                  {sessionConflict.activeSession.ipAddress && (
                    <span className="text-white/50">
                      ({sessionConflict.activeSession.ipAddress})
                    </span>
                  )}
                </div>
                {sessionConflict.activeSession.lastSeen && (
                  <div className="mt-1 text-xs text-white/50">
                    Dernière activité :{" "}
                    {new Date(sessionConflict.activeSession.lastSeen).toLocaleString()}
                  </div>
                )}
              </div>
            )}

            <div className="mt-5 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => {
                  setSessionConflict(null);
                  setPin("");
                }}
                className="flex-1 rounded-2xl border border-white/10 bg-white/5 text-white font-semibold py-3 hover:bg-white/10 transition"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={async () => {
                  setLoading(true);
                  try {
                    const response = await submitPinLogin({ takeover: true });
                    if (!response.ok) {
                      const errorData = await response.json().catch(() => ({}));
                      toast.error(errorData.error || "Mot de passe incorrect");
                      setPin("");
                      setSessionConflict(null);
                      return;
                    }
                    const data = await response.json();
                    const loggedUser = forceSuperadmin
                      ? { ...data.user, role: "superadmin" }
                      : data.user;
                    const userKey = forceSuperadmin
                      ? "moo_user_superadmin"
                      : "moo_user";
                    const tokenKey = forceSuperadmin
                      ? "moo_token_superadmin"
                      : "moo_token";
                    localStorage.setItem(userKey, JSON.stringify(loggedUser));
                    localStorage.setItem(tokenKey, data.token);
                    setSessionConflict(null);
                    onLogin(loggedUser, data.token);
                  } catch (_e) {
                    toast.error("Mot de passe incorrect");
                    setPin("");
                    setSessionConflict(null);
                  } finally {
                    setLoading(false);
                  }
                }}
                className="flex-1 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-400 text-slate-900 font-semibold py-3 hover:shadow-lg hover:shadow-amber-500/30 transition"
              >
                Prendre la main
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
